/**
 * LinkedIn / X post drafts generated from an event's own info and its
 * currently-confirmed speaker(s). Same {{field}} merge notation as the
 * email templates (see templates.js) — kept in a separate file since the
 * content and constraints (X's length limit) are genuinely different from
 * an email draft, not just a different template set on the same data.
 */

const X_CHAR_LIMIT = 280;

function merge(template, data) {
  return template.replace(/\{\{#if (\w+)\}\}([\s\S]*?)\{\{\/if\}\}/g, (_, key, inner) => (data[key] ? inner : ''))
    .replace(/\{\{(\w+)\}\}/g, (_, key) => (data[key] != null ? String(data[key]) : ''));
}

/**
 * @param {object} event
 * @param {object[]} engagements  This event's engagements (any status)
 * @param {object[]} speakers     Full speaker records, matched to engagements by speakerId
 */
function buildSocialContext(event, engagements = [], speakers = []) {
  const speakersById = Object.fromEntries(speakers.map((s) => [s.id, s]));
  const confirmed = engagements.filter((e) => !['Declined', 'Cancelled'].includes(e.status));
  const named = confirmed
    .map((e) => ({ speaker: speakersById[e.speakerId], talkTitle: e.talkTitle }))
    .filter((x) => x.speaker);

  const speakerNames = named.length
    ? named.map((n) => `${n.speaker.firstName} ${n.speaker.lastName}`).join(' & ')
    : '';
  const talkTitle = named.length === 1 ? named[0].talkTitle : '';
  const hasSpeaker = named.length > 0;

  return {
    eventName: event?.name ?? '',
    eventDate: event?.date
      ? new Date(event.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      : '',
    eventDateShort: event?.date
      ? new Date(event.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : '',
    eventFormat: event?.format ?? '',
    eventLocation: event?.location ?? '',
    pduInfo: event?.pduInfo ?? '',
    speakerNames,
    talkTitle,
    hasSpeaker,
  };
}

const LINKEDIN_TEMPLATES = {
  withSpeaker: `🎙️ Upcoming Chapter Event: {{eventName}}

Join us on {{eventDate}} ({{eventFormat}}) as {{speakerNames}} shares insights on "{{talkTitle}}".

📍 {{eventLocation}}
{{#if pduInfo}}🎓 {{pduInfo}}
{{/if}}
Don't miss this opportunity to learn and connect with fellow professionals!

#ProjectManagement #PMI #ProfessionalDevelopment`,

  saveTheDate: `📅 Save the Date: {{eventName}}

Mark your calendars for {{eventDate}} ({{eventFormat}}) — details and speaker lineup coming soon!

📍 {{eventLocation}}
{{#if pduInfo}}🎓 {{pduInfo}}
{{/if}}
#ProjectManagement #PMI #ProfessionalDevelopment`,
};

const X_TEMPLATES = {
  withSpeaker: `🎙️ {{eventName}} — {{eventDateShort}}
{{speakerNames}} on "{{talkTitle}}"
📍 {{eventLocation}}
#PMI #ProjectManagement`,

  saveTheDate: `📅 Save the date! {{eventName}} — {{eventDateShort}}
📍 {{eventLocation}}
Details soon. #PMI #ProjectManagement`,
};

/**
 * @param {'linkedin'|'x'} platform
 * @returns {{ text: string, charCount: number, overLimit: boolean }}
 */
export function generateSocialPost(platform, event, engagements = [], speakers = []) {
  const ctx = buildSocialContext(event, engagements, speakers);
  const variant = ctx.hasSpeaker ? 'withSpeaker' : 'saveTheDate';
  const templates = platform === 'x' ? X_TEMPLATES : LINKEDIN_TEMPLATES;
  const text = merge(templates[variant], ctx).replace(/\n{3,}/g, '\n\n').trim();
  return {
    text,
    charCount: text.length,
    overLimit: platform === 'x' && text.length > X_CHAR_LIMIT,
  };
}

export { X_CHAR_LIMIT };
